import { useEffect, useState } from 'react';

interface UseCountdownTimerOptions {
  initialSeconds: number | null;
  onComplete?: () => void;
}

interface UseCountdownTimerReturn {
  seconds: number | null;
  isRunning: boolean;
  start: () => void;
  pause: () => void;
  reset: (newSeconds?: number) => void;
  finish: () => void;
}

const useCountdownTimer = ({ initialSeconds, onComplete }: UseCountdownTimerOptions): UseCountdownTimerReturn => {
  const [seconds, setSeconds] = useState<number | null>(initialSeconds);
  const [isRunning, setIsRunning] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);

  useEffect(() => {
    if (seconds !== null && seconds > 0 && isRunning) {
      const interval = setInterval(() => {
        setSeconds((prevSeconds) => (prevSeconds !== null ? prevSeconds - 1 : 0));
      }, 1000);

      return () => clearInterval(interval);
    } else if (seconds === 0 && !hasCompleted) {
      setIsRunning(false);
      setHasCompleted(true);
      if (onComplete) onComplete();
    }
  }, [seconds, isRunning, onComplete, hasCompleted]);

  const start = () => {
    setIsRunning(true);
    setHasCompleted(false);
  };
  const pause = () => setIsRunning(false);
  const reset = (newSeconds?: number) => {
    setIsRunning(false);
    setSeconds(newSeconds ?? initialSeconds);
    setHasCompleted(false);
  };

  const finish = () => {
    setIsRunning(false);
    setSeconds(0);

    setHasCompleted(true);
  };

  return { seconds, isRunning, start, pause, reset, finish };
};

export default useCountdownTimer;
